'use client';

import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';

type TGlobalInternalErrorBoundaryProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export const GlobalInternalErrorBoundary = ({
  error,
  reset,
}: TGlobalInternalErrorBoundaryProps) => {
  const [sentryEventId, setSentryEventId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    Sentry.withScope(scope => {
      scope.setTag('error_type', 'critical');
      scope.setTag('error_origin', 'root_error_boundary');
      if (error.digest) {
        scope.setExtras({ error_server_digest: error.digest });
      }
      scope.setExtras({ error_stack: error.stack });
      if ('cause' in error) {
        scope.setExtras({ error_cause: error.cause });
      }
      const eventId = Sentry.captureException(error);

      setSentryEventId(eventId);
    });
  }, [error]);

  const isProd = process.env.APP_ENV === 'production';

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-mx-auto tw-w-full sm:tw-w-[80rem] tw-h-screen tw-p-1.2 tw-bg-neutral-light-100">
      {!isProd && (
        <div className="tw-w-full tw-mb-2.4">
          <h2 className="tw-font-subtitle2 tw-mb-1.6">
            Development error message:
          </h2>
          <p>Name: {error.name}</p>
          <p className="tw-mb-1.6">Message: {error.message}</p>
          <p className="tw-font-link1">Check the console for more details.</p>
        </div>
      )}
      <div className="tw-flex tw-flex-col tw-w-full tw-h-full md:tw-flex-row sm:tw-h-[35rem]">
        <div
          className="tw-flex-shrink-0 tw-bg-center tw-bg-cover tw-w-[13.1rem] tw-h-[19rem] sm:tw-w-[25rem] sm:tw-h-[35rem]"
          style={{
            backgroundImage:
              'url("https://static.crehana.com/static/img/crehana-cat-in-jar.png")',
          }}
        />
        <div className="tw-mt-1.2 md:tw-mt-0" style={{ lineHeight: '1.5' }}>
          <h2
            style={{
              fontSize: 24,
            }}
          >
            Oops, we have a problem...
          </h2>
          <br />
          <p className="tw-mt-0.4">
            Our team has just been notified and is already actively working to
            address the issue. If you would like to assist, please provide us
            with details on what transpired. Thank you for your cooperation.
          </p>
          <br />

          <button
            type="button"
            className="tw-btn-primary-s"
            onClick={() => {
              // This will show up a dialog like this https://docs.sentry.io/static/571cee86a7dba33e444eb629282ceae8/00d43/user_feedback_widget.png
              Sentry.showReportDialog({
                eventId: sentryEventId,
                title: 'Oops, we have a problem...',
                subtitle2:
                  'If you wish to assist, please provide us with details on what transpired next.',
              });
            }}
          >
            Provide details of the error.
          </button>
          <button
            onClick={() => reset()}
            className="tw-block tw-mt-1.6 tw-text-primary-light-500"
            type="button"
          >
            Alternatively, you can try loading the page again by clicking here.
          </button>
        </div>
      </div>
    </div>
  );
};
